import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { usePricingContext } from 'modules/onboarding/components/Pricing/PricingContext'
import type Plan from 'modules/onboarding/lib/pricing/plans/Plan'

import * as styles from './index.vanilla.css'

const FeatureWithTooltip = ({
  title,
  tooltip,
  isSelected,
}: {
  title: string
  tooltip: string
  isSelected: boolean
}) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt
      label={tooltip}
      attachment="bottom left"
      targetAttachment="top center"
      open={open}
      toggle={toggle}
    >
      <span>
        <Text.Normal
          className={styles.textWithToolTip}
          color={isSelected ? 'grey1000' : 'grey700'}
          weight="medium"
        >
          {title}
        </Text.Normal>
      </span>
    </Tooltip.Alt>
  )
}

const ItemsColumn = ({ items }: { items: Plan[] }) => {
  return (
    <Box gap="s8" style={{ flex: '1' }}>
      {items.map((plan) => (
        <Box key={plan.title} flexDirection="row" gap="s4" alignItems="center">
          <Box flexNoShrink>
            <Icon.Medium icon="check" color={plan.isSelected ? 'viridian1000' : 'grey200'} />
          </Box>
          <FeatureWithTooltip
            title={plan.title}
            tooltip={plan.description}
            isSelected={plan.isSelected}
          />
        </Box>
      ))}
    </Box>
  )
}

const FeaturesSection = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { allPlans } = usePricingContext()

  const modules = [...allPlans.normalPlans]
    .filter((module) => module.segmentationOption)
    .sort((a, b) => (b.isSelected ? 1 : -1) - (a.isSelected ? 1 : -1))
  const firstColumn = modules.slice(0, modules.length / 2)
  const secondColumn = modules.slice(modules.length / 2, modules.length)

  return (
    <Box
      padding="s48"
      background="grey200"
      justifyContent="spaceBetween"
      borderRadius={{ topRight: 'abs016', bottomRight: 'abs016' }}
      maxWidth="full"
      style={{ flex: '1' }}
    >
      <Box>
        <Text.Header1 color="grey800">
          {i18n.t('pricing_plans.personalised_hub.features_section.title')}
        </Text.Header1>
        <Box paddingY="s32" gap="s16">
          <Box flexDirection="row" gap="s4" flexWrap="wrap">
            {allPlans.corePlans.map((plan) => (
              <FeatureWithTooltip
                key={plan.title}
                title={i18n.t('pricing_plans.personalised_hub.features_section.text', {
                  planType: plan.activeVariantType,
                })}
                tooltip={plan.description}
                isSelected
              />
            ))}
            <Text.Normal color="grey800" weight="medium">
              {i18n.t('pricing_plans.personalised_hub.features_section.text_2')}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" justifyContent="spaceBetween" flexWrap="wrap" gap="s8">
            <ItemsColumn items={firstColumn} />
            <ItemsColumn items={secondColumn} />
          </Box>
        </Box>
      </Box>
      <Box
        flexDirection="row"
        gap="s8"
        cursor="pointer"
        onClick={() => navigate(navigation.upgradePlan.customBundles.fullPath())}
      >
        <Text.Normal color="grey1000" weight="medium">
          {i18n.t('pricing_plans.personalised_hub.features_section.link')}
        </Text.Normal>
        <Icon.Medium icon="arrow-right" color="grey800" />
      </Box>
    </Box>
  )
}

export default FeaturesSection
